import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import type { CommunityStructure, OnboardingConfig } from "@atrium/shared";
import { api, errorMessage } from "../../lib/api";
import { cn } from "../../lib/cn";
import { CommunityIcon } from "../../components/ui/Avatar";
import { Button } from "../../components/ui/Button";
import { Dialog } from "../../components/ui/Dialog";
import { IconCheck, IconHandshake } from "../../components/icons";

type Step = { kind: "welcome" } | { kind: "rules" } | { kind: "question"; index: number } | { kind: "done" };

const dismissedKey = (communityId: string) => `atrium:onboarding-later:${communityId}`;

/**
 * The focused onboarding flow: welcome → rules → questions → done, landing in the
 * intro channel. `preview` walks through a (possibly unsaved) config without submitting.
 */
export function OnboardingFlow({
  structure,
  open,
  onOpenChange,
  preview,
  config: configProp,
}: {
  structure: CommunityStructure;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  preview?: boolean;
  config?: OnboardingConfig;
}) {
  const c = structure.community;
  const config = configProp ?? c.onboarding;
  const qc = useQueryClient();
  const navigate = useNavigate();
  const questions = config.questions ?? [];
  const steps = useMemo<Step[]>(
    () => [
      { kind: "welcome" },
      ...(config.requireRules && c.rules ? [{ kind: "rules" } as Step] : []),
      ...questions.map((_, index) => ({ kind: "question", index }) as Step),
      { kind: "done" },
    ],
    [config.requireRules, c.rules, questions],
  );
  const [at, setAt] = useState(0);
  const [accepted, setAccepted] = useState(false);
  const [answers, setAnswers] = useState<Record<string, string[]>>({});
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAt(0);
    setAccepted(false);
    setAnswers({});
    setError(null);
  }, [open]);

  const step = steps[Math.min(at, steps.length - 1)];
  const intro = structure.channels.find((ch) => ch.id === config.introChannelId);
  const progressSteps = steps.length - 1; // "done" isn't counted

  const pick = (qid: string, oid: string, multiple: boolean) =>
    setAnswers((cur) => {
      const list = cur[qid] ?? [];
      const next = multiple ? (list.includes(oid) ? list.filter((x) => x !== oid) : [...list, oid]) : list.includes(oid) ? [] : [oid];
      return { ...cur, [qid]: next };
    });

  const canContinue =
    step.kind === "rules" ? accepted : step.kind === "question" ? !questions[step.index].required || (answers[questions[step.index].id]?.length ?? 0) > 0 : true;

  const next = async () => {
    setError(null);
    if (steps[at + 1]?.kind !== "done") return setAt(at + 1);
    if (preview) return setAt(at + 1);
    setPending(true);
    try {
      await api.post(`/api/communities/${c.id}/onboarding`, { acceptRules: accepted || !(config.requireRules && c.rules), answers });
      sessionStorage.removeItem(dismissedKey(c.id));
      setAt(at + 1);
      // New channels arrive with the refreshed structure.
      void qc.invalidateQueries({ queryKey: ["structure", c.id] });
      void qc.invalidateQueries({ queryKey: ["community"] });
      void qc.invalidateQueries({ queryKey: ["my-communities"] });
    } catch (e) {
      setError(errorMessage(e, "Couldn't finish onboarding"));
    } finally {
      setPending(false);
    }
  };

  const finish = async () => {
    onOpenChange(false);
    if (preview) return;
    await qc.refetchQueries({ queryKey: ["structure", c.id] });
    const target = config.introChannelId ?? c.defaultChannelId;
    if (target) navigate(`/c/${c.id}/${target}`);
  };

  const later = () => {
    if (!preview) sessionStorage.setItem(dismissedKey(c.id), "1");
    onOpenChange(false);
  };

  const title =
    step.kind === "welcome"
      ? config.welcomeTitle || `Welcome to ${c.name}`
      : step.kind === "rules"
        ? "Community rules"
        : step.kind === "question"
          ? questions[step.index].title
          : "You're all set";

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => (o ? onOpenChange(o) : step.kind === "done" ? void finish() : later())}
      title={title}
      description={preview ? "Preview: nothing is saved." : undefined}
      size="lg"
      footer={
        step.kind === "done" ? (
          <Button variant="primary" onClick={() => void finish()} data-autofocus>
            {preview ? "Close preview" : intro ? `Go to #${intro.name}` : "Start exploring"}
          </Button>
        ) : (
          <>
            <span className="mr-auto text-xs text-fg-3 tabular" aria-live="polite">
              Step {at + 1} of {progressSteps}
            </span>
            {at > 0 ? (
              <Button variant="ghost" onClick={() => setAt(at - 1)}>
                Back
              </Button>
            ) : (
              <Button variant="ghost" onClick={later}>
                Later
              </Button>
            )}
            <Button variant="primary" onClick={() => void next()} disabled={!canContinue} loading={pending} data-autofocus>
              {steps[at + 1]?.kind === "done" ? "Finish" : "Continue"}
            </Button>
          </>
        )
      }
    >
      <div className="mb-4 flex gap-1" aria-hidden>
        {Array.from({ length: progressSteps }, (_, i) => (
          <span key={i} className={cn("h-1 flex-1 rounded-full transition-colors motion-reduce:transition-none", i <= at ? "bg-accent" : "bg-surface-3")} />
        ))}
      </div>

      {step.kind === "welcome" && (
        <div className="flex flex-col items-center py-4 text-center">
          <CommunityIcon src={c.iconUrl} name={c.name} size="lg" />
          {config.welcomeBody ? (
            <p className="mt-4 max-w-md whitespace-pre-wrap text-base text-fg-2">{config.welcomeBody}</p>
          ) : (
            <p className="mt-4 text-base text-fg-2">A few quick steps and you're in.</p>
          )}
        </div>
      )}

      {step.kind === "rules" && (
        <div className="space-y-4">
          <div className="max-h-[40vh] overflow-y-auto whitespace-pre-wrap rounded-lg border border-line bg-bg p-4 text-sm text-fg-2">{c.rules}</div>
          <label className="flex cursor-pointer items-center gap-2.5 text-sm text-fg">
            <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="size-4 accent-[var(--accent)]" />
            I've read and agree to the rules
          </label>
        </div>
      )}

      {step.kind === "question" &&
        (() => {
          const q = questions[step.index];
          const picked = answers[q.id] ?? [];
          return (
            <div>
              <p className="mb-3 text-sm text-fg-3">
                {q.multiple ? "Pick as many as you like" : "Pick one"}
                {q.required ? "" : " (optional)"}
              </p>
              <div role={q.multiple ? "group" : "radiogroup"} aria-label={q.title} className="grid gap-2 sm:grid-cols-2">
                {q.options.map((o) => {
                  const on = picked.includes(o.id);
                  return (
                    <button
                      key={o.id}
                      type="button"
                      role={q.multiple ? "checkbox" : "radio"}
                      aria-checked={on}
                      onClick={() => pick(q.id, o.id, !!q.multiple)}
                      className={cn(
                        "flex min-h-12 items-center gap-3 rounded-lg border px-3 py-2 text-left text-sm transition-colors motion-reduce:transition-none",
                        on ? "border-accent bg-accent-soft text-fg" : "border-line-strong text-fg-2 hover:bg-surface hover:text-fg",
                      )}
                    >
                      {o.emoji && (
                        <span className="text-lg leading-none" aria-hidden>
                          {o.emoji}
                        </span>
                      )}
                      <span className="flex-1">{o.label}</span>
                      {on && <IconCheck className="icon size-[18px] text-accent" aria-hidden />}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })()}

      {step.kind === "done" && (
        <div className="flex flex-col items-center py-6 text-center">
          <div className="flex size-12 items-center justify-center rounded-xl bg-accent-soft text-accent">
            <IconHandshake className="icon size-6" aria-hidden />
          </div>
          <p className="mt-3 text-base text-fg-2">{preview ? "That's the whole flow." : "Every channel is unlocked."}</p>
        </div>
      )}

      {error && (
        <p className="mt-3 text-sm text-danger" role="alert">
          {error}
        </p>
      )}
    </Dialog>
  );
}

const wasDismissed = (communityId: string) => {
  try {
    return !!sessionStorage.getItem(dismissedKey(communityId));
  } catch {
    return false;
  }
};

/**
 * Mounted for the whole community view. While the viewer still needs onboarding it
 * shows a slim bar with a way into the flow, which also opens by itself once per
 * session. The flow stays mounted after finishing so the last step can be read.
 */
export function OnboardingHost({ structure, banner = true }: { structure: CommunityStructure; banner?: boolean }) {
  const c = structure.community;
  const needs = c.needsOnboarding;
  const [open, setOpen] = useState(() => needs && !wasDismissed(c.id));
  useEffect(() => {
    if (needs && !wasDismissed(c.id)) setOpen(true);
  }, [needs, c.id]);
  const gated = (c.onboarding.lobbyChannelIds?.length ?? 0) > 0;
  return (
    <>
      {needs && banner && (
        <div className="flex shrink-0 items-center gap-3 border-b border-line bg-accent-soft px-4 py-2">
          <IconHandshake className="icon size-5 shrink-0 text-accent" aria-hidden />
          <p className="min-w-0 flex-1 truncate text-sm text-fg">{gated ? "Finish onboarding to unlock every channel" : "Finish setting up your membership"}</p>
          <Button size="sm" variant="primary" onClick={() => setOpen(true)}>
            Finish onboarding
          </Button>
        </div>
      )}
      {(needs || open) && <OnboardingFlow structure={structure} open={open} onOpenChange={setOpen} />}
    </>
  );
}
